import React, { useContext, useState, useEffect, useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import styled, { keyframes } from 'styled-components'
import { useWallet } from 'use-wallet'

import Pagination from 'react-js-pagination'
import Button from 'components/Button'
import Card from 'components/Card'
import CardContent from 'components/CardContent'
import ApproveTransferModal from 'components/ApproveTransferModal'
import CreateNewPoolModal from 'components/CreateNewPoolModal'
import WalletProviderModal from 'components/WalletProviderModal'
import TokenProviderModal from 'components/TokenProviderModal'
import Spacer from 'components/Spacer'
import Loader from 'components/Loader'
import Input from 'components/Input'
import Selector from 'components/Selector'
import Tooltip from 'components/Tooltip'
import useAllowance from 'hooks/useAllowance'
import useModal from 'hooks/useModal'
import {
  getPoolRewardText,
  getPoolBalanceText,
  getNextTicketBonus,
  getPoolTokenLogoURL,
  isUnnamed,
  getDrawBonus,
} from 'lottery/util'
import { AccountContext } from 'contexts/AccountProvider'
import { TokenContext } from 'contexts/TokenProvider'
import { PoolListContext } from 'contexts/PoolListProvider'
import { poolsPagePage } from 'lottery/constants'
import WalletUnlockButton from 'components/WalletUnlockButton'

const PoolCards = () => {
  const history = useHistory()
  const allowance = useAllowance()
  const { account } = useWallet()
  const { allTokens } = useContext(TokenContext)
  const [searchPoolType, setSearchPoolType] = useState(null)
  const [onPresentApproveTransferModal] = useModal(<ApproveTransferModal />)
  const [onPresentCreateNewPoolModal] = useModal(
    <CreateNewPoolModal history={history} />
  )
  const [onPresentWalletProviderModal] = useModal(<WalletProviderModal />)
  const [onPresentTokenProviderModal] = useModal(
    <TokenProviderModal
      searchPoolType={searchPoolType}
      setSearchPoolType={setSearchPoolType}
    />
  )
  const [activePage, setActivePage] = useState(1)
  const [searchPoolName, setSearchPoolName] = useState('')
  const { poolList } = useContext(PoolListContext)
  const [totalResult, setTotalResult] = useState(poolList.length)

  const handleCreateNewPool = async () => {
    if (!account) {
      onPresentWalletProviderModal()
      return
    }

    if (!allowance.toNumber()) {
      onPresentApproveTransferModal()
      return
    }

    onPresentCreateNewPoolModal()
    return
  }

  const handleTokenSelect = async () => {
    onPresentTokenProviderModal()
    return
  }

  const filteredPool = useMemo(() => {
    return (
      poolList &&
      poolList.length > 0 &&
      poolList
        .filter(
          (pool) =>
            pool &&
            pool.name !== null &&
            pool.name !== undefined &&
            pool.name.toLowerCase().lastIndexOf(searchPoolName.toLowerCase()) >
              -1
        )
        .filter((pool) => {
          if (searchPoolType !== null) {
            return (
              pool.token.toLowerCase() === searchPoolType.address.toLowerCase()
            )
          }
          return true
        })
        .slice(
          activePage === 1 ? 0 : (activePage - 1) * poolsPagePage,
          activePage * poolsPagePage
        )
        .map((pool) =>
          pool && pool.status === '1' ? (
            <PoolCard
              pool={pool}
              type="pool"
              key={pool.pid}
              allTokens={allTokens}
            />
          ) : null
        )
    )
  }, [searchPoolName, poolList, activePage, searchPoolType])

  const filterResultsCount = (searchName) => {
    let total = 0
    if (searchPoolName === '' && searchPoolType === null) {
      setTotalResult(poolList.length)
      return
    }
    for (const pool of poolList) {
      if (pool) {
        if (searchPoolName !== '' && searchPoolType !== null) {
          if (
            pool &&
            pool.name &&
            pool.name.toLowerCase().lastIndexOf(searchPoolName.toLowerCase()) >
              -1 &&
            pool.token === searchPoolType.address
          ) {
            total += 1
          }
        }
        if (searchPoolName !== '' && searchPoolType === null) {
          if (
            pool &&
            pool.name &&
            pool.name.toLowerCase().lastIndexOf(searchPoolName.toLowerCase()) >
              -1
          ) {
            total += 1
          }
        }
        if (searchPoolName === '' && searchPoolType !== null) {
          if (pool.token === searchPoolType.address) {
            total += 1
          }
        }
      }
      setTotalResult(total)
    }
  }

  useEffect(
    (searchName) => {
      filterResultsCount(searchName)
    },
    [searchPoolName, poolList, searchPoolType]
  )

  if (!allTokens || allTokens.length === 0) {
    return null
  }

  const handlePageChange = (pageNumber) => {
    setActivePage(pageNumber)
  }
  if (poolList && poolList.length > 0) {
    return (
      <>
        <StyledserachWrapper>
          <StyledRow>
            <Input
              placeholder="Search Pool"
              value={searchPoolName}
              onChange={(e) => setSearchPoolName(e.target.value)}
            />
            <Selector
              placeholder="Select Token"
              handleOpenMenu={handleTokenSelect}
              selectedOption={searchPoolType}
              startAdornment="Filter by Token"
            />
            <StyledButtonWrapper>
              <Button
                size="sm"
                text="Reset Filter"
                onClick={() => {
                  setSearchPoolType(null)
                  setSearchPoolName('')
                }}
              />
            </StyledButtonWrapper>
          </StyledRow>
        </StyledserachWrapper>
        <StyledCards>
          <StyledRow>
            <PoolCard type="new" onHandleCreateNewPool={handleCreateNewPool} />
            {filteredPool}
          </StyledRow>
        </StyledCards>
        <StyledpaginationWrapper>
          <Pagination
            activePage={activePage}
            linkClass="page-link"
            itemClass="page-item"
            itemsCountPerPage={poolsPagePage}
            totalItemsCount={totalResult}
            pageRangeDisplayed={5}
            onChange={handlePageChange}
          />
        </StyledpaginationWrapper>
      </>
    )
  }
  if (account && poolList.length == 0) {
    return (
      <>
        <Loader text="Waiting for the pools" />
      </>
    )
  }
  return <WalletUnlockButton handleUnlockClick={handleCreateNewPool} />
}

export const PoolCard = ({ pool, type, onHandleCreateNewPool, allTokens }) => {
  const history = useHistory()
  const { poolsContract } = useContext(AccountContext)
  const [drawBonus, setDrawBonus] = useState(0)
  const [isDrawable, setIsDrawable] = useState(false)
  const handleClickCard = () => {
    history.push(`pools/${pool.pid}`)
  }

  useEffect(() => {
    if (poolsContract && pool) {
      const isDrawableTmp =
        pool.status === '1' && pool.balance / pool.reward >= 1.2
      setIsDrawable(isDrawableTmp)
      getDrawBonus(pool, poolsContract).then((result) => {
        setDrawBonus(result)
      })
    }
  }, [poolsContract])

  if (type === 'new') {
    return (
      <StyledCardWrapper>
        <StyledCardAccent />
        <Card>
          <CardContent>
            <StyledContent>
              <StyledDetails></StyledDetails>
              <Spacer size="mg" />
              <Spacer size="sm" />
              <Button text="Create New Pool" onClick={onHandleCreateNewPool} />
            </StyledContent>
          </CardContent>
        </Card>
      </StyledCardWrapper>
    )
  }

  if (isUnnamed(pool)) {
    pool.name = getPoolRewardText(pool) + ' Jackpot'
  }

  return (
    <StyledCardWrapper onClick={handleClickCard}>
      <Card>
        <CardContent>
          <StyledContent>
            <StyledTitle>
              <img src={getPoolTokenLogoURL(pool)} height="60" alt="dice" />
            </StyledTitle>
            <StyledTitle>{pool.name}</StyledTitle>
            <StyledDetails>
              <StyledDetail>
                {isUnnamed(pool) ? '' : `Reward: ${getPoolRewardText(pool)}`}
              </StyledDetail>
              <StyledDetail>
                Current Bonus:{' '}
                <StyledBonusText>
                  {getNextTicketBonus(pool)} UBET
                  <Tooltip
                    text={`If you purchase 1 ticket right now, you'll get 1 UBET immediately <br/>
											as bonus. No matter whether you won the lottery. If you purchase <br/>more tickets,
											 the per ticket bonus decreases slightly after each ticket.`}
                  />
                </StyledBonusText>
              </StyledDetail>
              {isDrawable && (
                <StyledDetail>
                  Drawing Bonus:{' '}
                  <StyledBonusText>
                    {drawBonus} UBET
                    <Tooltip
                      text={`if you are the first one to draw this lottery pool,<br/> 
									you'll get ${drawBonus} UBET bonus". Also, we'll add a  <br/>flag to the pool
									card that indicates it's ready to draw`}
                    />
                  </StyledBonusText>
                </StyledDetail>
              )}
              <StyledDetail>Balance: {getPoolBalanceText(pool)}</StyledDetail>
            </StyledDetails>
          </StyledContent>
        </CardContent>
      </Card>
    </StyledCardWrapper>
  )
}

const StyledserachWrapper = styled.div`
  margin-bottom: 25px;
`
const StyledButtonWrapper = styled.div`
  width: 250px;
  height: 40px;
  margin-left: 20px;
`
const RainbowLight = keyframes`
  
	0% {
		background-position: 0% 50%;
	}
	50% {
		background-position: 100% 50%;
	}
	100% {
		background-position: 0% 50%;
	}
`
const StyledpaginationWrapper = styled.div`
  & > .pagination {
    display: inline-block;
    list-style-type: none;
    border-bottom: none;
    float: right;
  }
  & > .pagination li {
    float: left;
    padding: 8px 16px;
  }
  & > .pagination a {
    color: #5b3926;
    float: left;
    text-decoration: none;
  }

  & > .pagination li.active {
    background-color: #fadbcb;
    color: white;
    text-align: right;
  }
`
const StyledCardAccent = styled.div`
  background: linear-gradient(
    45deg,
    rgba(255, 0, 0, 1) 0%,
    rgba(255, 154, 0, 1) 10%,
    rgba(208, 222, 33, 1) 20%,
    rgba(79, 220, 74, 1) 30%,
    rgba(63, 218, 216, 1) 40%,
    rgba(47, 201, 226, 1) 50%,
    rgba(28, 127, 238, 1) 60%,
    rgba(95, 21, 242, 1) 70%,
    rgba(186, 12, 248, 1) 80%,
    rgba(251, 7, 217, 1) 90%,
    rgba(255, 0, 0, 1) 100%
  );
  background-size: 300% 300%;
  animation: ${RainbowLight} 2s linear infinite;
  border-radius: 12px;
  filter: blur(6px);
  position: absolute;
  top: -2px;
  right: -2px;
  bottom: -2px;
  left: -2px;
  z-index: -1;
`

const StyledCards = styled.div`
  width: 912px;
  display: flex;
  @media (max-width: 768px) {
    width: 100%;
  }
`

const StyledRow = styled.div`
  display: flex;
  margin-bottom: ${(props) => props.theme.spacing[4]}px;
  flex-flow: row wrap;
  @media (max-width: 768px) {
    width: 100%;
    flex-flow: column nowrap;
    align-items: center;
  }
`

const StyledCardWrapper = styled.div`
  display: flex;
  cursor: pointer;
  margin-right: 20px;
  margin-bottom: 20px;
  width: calc((900px - ${(props) => props.theme.spacing[4]}px * 2) / 3);
  position: relative;
`

const StyledTitle = styled.h4`
  color: ${(props) => props.theme.color.grey[600]};
  font-size: 24px;
  font-weight: 700;
  margin: ${(props) => props.theme.spacing[2]}px 0 0;
  padding: 0;
`

const StyledContent = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
`

const StyledDetails = styled.div`
  margin-top: ${(props) => props.theme.spacing[2]}px;
  text-align: left;
`

const StyledDetail = styled.div`
  color: ${(props) => props.theme.color.grey[500]};
`

const StyledBonusText = styled.span`
  white-space: nowrap;
`

export default PoolCards
